import axios from 'axios';

const API = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const path = window.location.pathname;
      if (!path.startsWith('/enroll') && !path.startsWith('/login')) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ===== AUTH =====
export const authAPI = {
  login: (email, mot_de_passe) => API.post('/auth/login', { email, mot_de_passe }),
  me: () => API.get('/auth/me'),
  updateProfile: (data) => API.put('/auth/profile', data),
  changePassword: (data) => API.put('/auth/password', data),
  changeEmail: (data) => API.put('/auth/email', data),
  register: (data) => API.post('/auth/register', data),
};

// ===== IMMOBILISATIONS =====
export const immobilisationsAPI = {
  getAll: (params) => API.get('/immobilisations', { params }),
  getById: (id) => API.get(`/immobilisations/${id}`),
  create: (data) => {
    const config = data instanceof FormData
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : {};
    return API.post('/immobilisations', data, config);
  },
  update: (id, data) => {
    const config = data instanceof FormData
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : {};
    return API.put(`/immobilisations/${id}`, data, config);
  },
  delete: (id) => API.delete(`/immobilisations/${id}`),
  getStats: () => API.get('/immobilisations/stats/dashboard'),
  getMapData: () => API.get('/immobilisations/map/all'),
};

// ===== ENROLL (PUBLIC) =====
export const enrollAPI = {
  check: (id) => API.get(`/enroll/${id}`),
  enroll: (id, data, enrollToken = null) => {
    const config = data instanceof FormData
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : {};
    const url = enrollToken ? `/enroll/${id}?t=${encodeURIComponent(enrollToken)}` : `/enroll/${id}`;
    return API.post(url, data, config);
  },
  getInfo: (id) => API.get(`/enroll/${id}/info`),
};

// ===== QR CODE =====
// Pour les téléchargements PDF/HTML, on demande au backend un download-token
// court-vie scope (kind + ids), au lieu d'envoyer le JWT principal en query string.
async function fetchDownloadLink(kind, ids) {
  const params = new URLSearchParams();
  params.set('kind', kind);
  ids.forEach((id) => params.append('ids', id));
  const res = await API.get('/qrcode/download-link?' + params.toString());
  return res.data.url;
}

export const qrcodeAPI = {
  generate: (id) => `/api/qrcode/generate/${id}`,
  label: (id) => `/api/qrcode/label/${id}`,
  batch: (ids) => API.get("/qrcode/batch", { params: { ids } }),
  generateBlank: (data) => API.post("/qrcode/generate-blank", data),
  getBlanks: (params) => API.get("/qrcode/blanks", { params }),
  deleteBlank: (id) => API.delete("/qrcode/blank/" + id),
  // batchLabelsLink/batchPdfLink: appellent /download-link et retournent l'URL signée
  batchLabelsLink: (ids) => fetchDownloadLink('labels', ids),
  batchPdfLink: (ids) => fetchDownloadLink('pdf', ids),
};

// ===== CATEGORIES =====
export const categoriesAPI = {
  getAll: () => API.get('/categories'),
  getById: (id) => API.get(`/categories/${id}`),
  create: (data) => API.post('/categories', data),
  update: (id, data) => API.put(`/categories/${id}`, data),
  delete: (id) => API.delete(`/categories/${id}`),
};

// ===== SERVICES =====
export const servicesAPI = {
  getAll: () => API.get('/services'),
  getById: (id) => API.get(`/services/${id}`),
  create: (data) => API.post('/services', data),
  update: (id, data) => API.put(`/services/${id}`, data),
  delete: (id) => API.delete(`/services/${id}`),
};

// ===== USERS =====
export const usersAPI = {
  getAll: () => API.get('/users'),
  getById: (id) => API.get(`/users/${id}`),
  create: (data) => authAPI.register(data),
  update: (id, data) => API.put(`/users/${id}`, data),
  delete: (id) => API.delete(`/users/${id}`),
};

// ===== HISTORIQUE =====
export const historiqueAPI = {
  getAll: (params) => API.get('/historique', { params }),
};

// ===== SCANS =====
export const scansAPI = {
  getAll: (params) => API.get('/scans', { params }),
};

export default API;
